import { FC, ReactNode } from 'react'
import { Alert, AlertTitle, Chip } from '@mui/material'
import { authStore } from '@/store/authStore'
import { tokens } from '@/theme'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import {
  ConfiguracaoPrivacidade,
  PAPEL_DPO,
  STATUS_EXPURGO_LABEL,
  StatusExpurgo,
} from '@/types/privacidade'

/**
 * Peças comuns das telas de Privacidade (LGPD) — AN-12 §12, DEV-AB.
 */

export interface AcessoPrivacidade {
  /** `sub` de quem está logado (para "o mesmo usuário não aprova duas vezes"). */
  usuarioId: string | null
  admin: boolean
  dpo: boolean
  /** ADMIN ou DPO: o back-office de privacidade é só deles (AN-12 §7.3). */
  permitido: boolean
  /** Sessão ainda não hidratada — não decide "sem permissão" antes da hora. */
  semSessao: boolean
}

export const acessoPrivacidade = (
  usuario: { id?: string; permissoes?: string[] } | null | undefined
): AcessoPrivacidade => {
  const p = (usuario?.permissoes ?? []).map((x) => x.toUpperCase())
  const admin = p.includes('ADMIN')
  const dpo = p.includes(PAPEL_DPO)
  return {
    usuarioId: usuario?.id ?? null,
    admin,
    dpo,
    permitido: admin || dpo,
    semSessao: !usuario,
  }
}

export const useAcessoPrivacidade = (): AcessoPrivacidade => {
  const usuario = authStore((s) => s.usuario)
  return acessoPrivacidade(usuario)
}

/** Código de negócio do envelope de erro do `api` (`{ message, status, data: { errorCode } }`). */
export const codigoErro = (e: unknown): string | undefined => {
  const data = (e as { data?: { errorCode?: unknown } } | null)?.data
  return typeof data?.errorCode === 'string' ? data.errorCode : undefined
}

const MENSAGENS: Record<string, string> = {
  PREVIA_DESATUALIZADA:
    'O conjunto de arquivos mudou desde a prévia (apareceu versão ou cópia nova). Recarregue o expurgo e revise antes de aprovar.',
  DOCUMENTO_EM_RETENCAO_LEGAL: 'Há retenção legal (legal hold) sobre um dos documentos — o expurgo foi bloqueado.',
  DOCUMENTO_ATIVO: 'Um dos documentos ainda está ativo. Remova-o (exclusão lógica) antes de pedir o expurgo.',
  CATEGORIA_NAO_EXPURGAVEL: 'Um dos documentos é de categoria com obrigação de retenção (não expurgável).',
  DOCUMENTO_EM_OUTRO_EXPURGO: 'Um dos documentos já está em outro expurgo aberto.',
  RESERVA_NAO_SINCRONIZADA: 'Reserva ainda não sincronizada: não dá para conferir retenção legal. Tente mais tarde.',
  DOCUMENTO_EXPURGADO: 'Arquivo expurgado — não está mais disponível.',
  DOCUMENTO_JA_EXPURGADO: 'Um dos documentos já foi expurgado.',
  DOCUMENTO_NAO_ENCONTRADO: 'Um dos documentos não foi encontrado.',
  EXPURGO_COM_BLOQUEIOS: 'O expurgo tem bloqueios — recalcule a prévia para ver quais.',
  EXPURGO_SEM_ITENS: 'Nenhum documento removido encontrado para este alvo.',
  APROVADOR_NAO_DISTINTO:
    'A mesma pessoa não aprova duas vezes: solicitante, 1ª e 2ª aprovação precisam ser pessoas distintas.',
  PAPEL_NAO_AUTORIZADO_NA_APROVACAO: 'A 1ª aprovação é do DPO e a 2ª do ADMIN.',
  EXPURGO_ESTADO_INVALIDO: 'O expurgo mudou de estado. Recarregue.',
  CONFLITO_VERSAO: 'Outra pessoa alterou este expurgo ao mesmo tempo. Recarregue e tente de novo.',
  SOMENTE_SOLICITANTE_CANCELA: 'Só quem pediu o expurgo pode cancelá-lo.',
  POLITICA_VERSAO_CONCORRENTE: 'Outra versão desta política foi registrada ao mesmo tempo. Recarregue.',
  RETENCAO_LEGAL_JA_ATIVA: 'Já existe retenção legal ativa para este alvo.',
  RETENCAO_LEGAL_JA_LIBERADA: 'Esta retenção legal já foi liberada.',
  // auth — solicitações do titular (DEV-AA)
  DUPLA_CHECAGEM_VIOLADA:
    'Dupla checagem: quem encaminhou a solicitação para execução (DPO) não pode executá-la. Outra pessoa (ADMIN) precisa executar.',
  SOLICITACAO_NAO_EXECUTAVEL: 'Esta solicitação não é de anonimização executável.',
  ESTADO_NAO_PERMITE_EXECUCAO: 'A solicitação não está aguardando execução.',
  ANONIMIZACAO_EXIGE_EXECUCAO:
    'Anonimização só é dada como atendida pela execução (botão do ADMIN), não por transição manual.',
  FUNDAMENTO_OBRIGATORIO: 'Informe o fundamento na nota para esta transição.',
  TRANSICAO_SOLICITACAO_INVALIDA: 'Transição recusada: a solicitação só anda para frente. Recarregue e tente de novo.',
  ESTADO_INALTERADO: 'A solicitação já está neste estado.',
  SOLICITACAO_ENCERRADA: 'Solicitação encerrada — não aceita mais alterações.',
  SOLICITACAO_NAO_CANCELAVEL: 'Só dá para cancelar enquanto a solicitação está recebida ou em análise.',
  CANAL_INVALIDO: 'Canal inválido: registre como e-mail ou outro.',
  DOCUMENTO_TITULAR_INVALIDO: 'CPF do titular inválido.',
  IDENTIFICACAO_DOCUMENTO_DESABILITADA:
    'Identificação por CPF desabilitada neste ambiente (sem chave de pseudonimização).',
}

/** Mensagem para o usuário: por código conhecido, por status, ou a do servidor. */
export const mensagemErroPrivacidade = (e: unknown, padrao: string): string => {
  const codigo = codigoErro(e)
  if (codigo && MENSAGENS[codigo]) return MENSAGENS[codigo]
  const falha = e as { status?: number; message?: string } | null
  if (falha?.status === 403) return 'Sem permissão para esta ação (papel ou pessoa — a mesma pessoa não aprova duas vezes).'
  if (falha?.status === 409 && !codigo) return 'Conflito: o registro mudou desde que a tela o leu. Recarregue e tente de novo.'
  return falha?.message || padrao
}

const COR_STATUS: Record<StatusExpurgo, string> = {
  PENDENTE: tokens.statusWarning,
  APROVADO_1: tokens.statusWarning,
  APROVADO: tokens.brandPrimary,
  EXECUTANDO: tokens.brandPrimary,
  EM_EXECUCAO: tokens.brandPrimary,
  AGUARDANDO_COPIAS: tokens.brandPrimary,
  CONCLUIDO: tokens.statusSuccess,
  REJEITADO: tokens.textSecondary,
  CANCELADO: tokens.textSecondary,
  BLOQUEADO_RETENCAO: tokens.statusDanger,
  ERRO: tokens.statusDanger,
}

/**
 * Status do expurgo; `APROVADO` com a flag desligada vira "Aprovado — aguardando
 * habilitação" (AN-12 §5.3): nada é apagado enquanto o expurgo físico estiver off.
 */
export const ChipStatusExpurgo: FC<{
  status: StatusExpurgo
  expurgoFisicoHabilitado?: boolean
  /** `aguardandoHabilitacao` do servidor — vale mesmo sem a configuração lida. */
  aguardandoHabilitacao?: boolean
}> = ({ status, expurgoFisicoHabilitado, aguardandoHabilitacao }) => {
  const rotulo =
    (status === 'APROVADO' && expurgoFisicoHabilitado === false) || aguardandoHabilitacao
      ? 'Aprovado — aguardando habilitação'
      : STATUS_EXPURGO_LABEL[status] ?? status
  return (
    <Chip
      size="small"
      variant="outlined"
      label={rotulo}
      sx={{ borderColor: COR_STATUS[status], color: COR_STATUS[status], fontWeight: 600 }}
    />
  )
}

/**
 * Banner da flag de expurgo físico (ONDA5, decisão 2). Só afirma o que o backend expõe:
 * sem configuração lida, não mostra nada (não inventa estado).
 */
export const BannerExpurgoFisico: FC<{ configuracao: ConfiguracaoPrivacidade | null }> = ({ configuracao }) => {
  if (!configuracao || configuracao.expurgoFisicoHabilitado !== false) return null
  return (
    <Alert severity="warning" sx={{ mb: 2 }} data-testid="banner-expurgo-desativado">
      <AlertTitle>Expurgo físico desativado: aprovações não apagam arquivos</AlertTitle>
      Aguardando aceite do Jurídico/DPO. Os expurgos seguem o fluxo até "Aprovado — aguardando habilitação" e
      nenhum arquivo é removido enquanto o expurgo físico estiver desligado.
    </Alert>
  )
}

/** "usuário 1a2b3c4d" — a tela não resolve nomes de operador (não há endpoint para isso aqui). */
export const rotuloAtor = (sub: string | null | undefined, eu: string | null): string => {
  if (!sub) return '—'
  if (sub === 'SISTEMA') return 'Sistema'
  if (eu && sub === eu) return 'você'
  return `usuário ${sub.slice(0, 8)}`
}

/**
 * Só ADMIN e DPO (AN-12 §7.3). Quem não tem o papel vê "sem permissão" e o conteúdo nem é
 * montado — nenhuma chamada ao servidor (que responderia 403 de qualquer jeito).
 */
export const GuardaPrivacidade: FC<{ children: ReactNode; detalhe?: string }> = ({ children, detalhe }) => {
  const acesso = useAcessoPrivacidade()
  if (acesso.semSessao) return null
  if (!acesso.permitido) {
    return (
      <EstadoDeLista
        estado="semPermissao"
        detalhe={detalhe ?? 'Privacidade (LGPD) é restrita ao DPO e ao administrador.'}
      />
    )
  }
  return <>{children}</>
}
